import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Trash2, Download, RefreshCw, FileSpreadsheet, Search, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '../utils/api';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

function relativeTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  return d.toISOString().slice(0, 10);
}

async function downloadFile(url, filename) {
  const res = await apiFetch(url);
  if (!res.ok) throw new Error(await res.text().catch(() => 'Export failed'));
  const blob = await res.blob();
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
}

export default function UploadHistorySection({ refreshKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/api/reviews/uploads');
      if (!res.ok) {
        setItems([]);
        return;
      }
      const data = await res.json();
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch (_) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems, refreshKey]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => {
      const hay = [
        it.filename,
        it.platform,
        it.uploader_email,
        ...(it.brands || []),
        ...(it.product_names || []),
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [items, filter]);

  const handleExport = async (item, format) => {
    setBusyId(item.upload_id + ':' + format);
    try {
      const ext = format === 'xlsx' ? 'xlsx' : 'csv';
      const safe = (item.filename || 'reviews').replace(/[^\w.-]+/g, '_');
      const url = `/api/reviews/uploads/${encodeURIComponent(item.upload_id)}/export?format=${format}`;
      await downloadFile(url, `${safe.replace(/\.(csv|xlsx|xls)$/i, '')}_export.${ext}`);
      toast.success(`Exported as ${ext.toUpperCase()}`);
    } catch (err) {
      toast.error(`Export failed`);
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const item = deleteTarget;
    setBusyId(item.upload_id + ':delete');
    try {
      const res = await apiFetch(`/api/reviews/uploads/${encodeURIComponent(item.upload_id)}`, { method: 'DELETE' });
      if (!res.ok) {
        toast.error('Delete failed');
        return;
      }
      const data = await res.json().catch(() => ({}));
      toast.success(`Deleted ${data.reviews_deleted ?? item.review_count} reviews`);
      setDeleteTarget(null);
      await fetchItems();
    } catch (_) {
      toast.error('Delete failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card data-testid="upload-history-card" className="mt-6">
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileSpreadsheet className="w-4 h-4 text-[#2eb4ac]" />
            Upload History
          </CardTitle>
          <CardDescription>
            Every review upload logged for audit — export or delete to wipe those reviews.
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Filter…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="pl-8 h-8 w-44 text-xs"
              data-testid="upload-history-filter"
            />
          </div>
          <Button variant="ghost" size="sm" onClick={fetchItems} data-testid="upload-history-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="upload-history-table">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border/50">
                <th className="text-left py-2 pr-3 font-medium">When</th>
                <th className="text-left py-2 pr-3 font-medium">File</th>
                <th className="text-left py-2 pr-3 font-medium">Platform</th>
                <th className="text-left py-2 pr-3 font-medium">Brand(s)</th>
                <th className="text-right py-2 pr-3 font-medium">Reviews</th>
                <th className="text-left py-2 pr-3 font-medium">Uploader</th>
                <th className="text-right py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="text-center py-6 text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading history…
                </td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={7} className="text-center py-6 text-muted-foreground text-xs" data-testid="upload-history-empty">
                  {items.length === 0 ? 'No uploads recorded yet' : 'No uploads match your filter'}
                </td></tr>
              )}
              {!loading && filtered.map((it) => {
                const rowKey = it.upload_id;
                const isBusy = busyId && busyId.startsWith(rowKey + ':');
                return (
                  <tr key={rowKey} className="border-b border-border/30 hover:bg-muted/20" data-testid={`upload-row-${rowKey}`}>
                    <td className="py-2 pr-3">
                      <div className="text-xs">{relativeTime(it.uploaded_at)}</div>
                      <div className="text-[10px] text-muted-foreground">{it.uploaded_at?.slice(0, 10)}</div>
                    </td>
                    <td className="py-2 pr-3 max-w-[240px]">
                      <div className="text-xs font-medium truncate" title={it.filename}>{it.filename}</div>
                      {it.is_legacy && (
                        <Badge variant="secondary" className="mt-0.5 text-[9px] bg-amber-500/10 text-amber-400 border-amber-500/20">Legacy</Badge>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      <Badge variant="outline" className="text-[10px]">{it.platform || '—'}</Badge>
                    </td>
                    <td className="py-2 pr-3 max-w-[220px]">
                      <div className="text-xs truncate" title={(it.brands || []).join(', ')}>
                        {(it.brands || []).slice(0, 2).join(', ') || '—'}
                        {(it.brands || []).length > 2 && <span className="text-muted-foreground"> +{it.brands.length - 2}</span>}
                      </div>
                    </td>
                    <td className="py-2 pr-3 text-right">
                      <span className="text-xs font-semibold">{it.review_count?.toLocaleString?.() ?? it.review_count}</span>
                    </td>
                    <td className="py-2 pr-3">
                      <span className="text-xs text-muted-foreground">{it.uploader_email || '—'}</span>
                    </td>
                    <td className="py-2 text-right whitespace-nowrap">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-7 px-2" data-testid={`upload-export-${rowKey}`} disabled={isBusy}>
                            <Download className="w-3.5 h-3.5 mr-1" /> Export
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleExport(it, 'csv')} data-testid={`upload-export-csv-${rowKey}`}>as CSV</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleExport(it, 'xlsx')} data-testid={`upload-export-xlsx-${rowKey}`}>as Excel (.xlsx)</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => setDeleteTarget(it)}
                        disabled={isBusy}
                        data-testid={`upload-delete-${rowKey}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="upload-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete upload &amp; wipe reviews?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete{' '}
              <span className="font-semibold text-destructive">{deleteTarget?.review_count?.toLocaleString?.() ?? deleteTarget?.review_count}</span>{' '}
              reviews from{' '}
              <span className="font-semibold">{deleteTarget?.platform}</span>
              {deleteTarget?.brands?.length ? <> for <span className="font-semibold">{deleteTarget.brands.join(', ')}</span></> : null}.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="upload-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="upload-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
